import styled from '@emotion/styled';
import { PlotTooltip, usePlotTooltip } from '@resistapp/client/components/tooltips/plot-tooltip';
import { useResearchContext } from '@resistapp/client/contexts/research-context';
import { useSampleDataContext } from '@resistapp/client/contexts/sample-data-context';
import { BarDatum, BoxDatum } from '@resistapp/client/data-utils/plot-data/research-plot-data';
import { useExperimentalMetric } from '@resistapp/client/hooks/use-experimental-metric';
import { useNormalisationMode, useXAxisMode } from '@resistapp/client/hooks/use-query-filters/use-query-filters';
import { getKeyPressesFromMouseClick } from '@resistapp/client/utils/general';
import { GeneGrouping } from '@resistapp/common/assays';
import { friendlyDate, StandardDateFormat } from '@resistapp/common/friendly';
import { isAyNormalisationMode } from '@resistapp/common/normalisation-mode';
import { FullProject, XAxisMode } from '@resistapp/common/types';
import { AxisScale } from '@visx/axis';
import { Glyph } from '@visx/glyph';
import { Group } from '@visx/group';
import { BoxPlot } from '@visx/stats';
import { UseTooltipInPortal } from '@visx/tooltip/lib/hooks/useTooltipInPortal';
import { ScaleLinear, ScaleOrdinal } from 'd3-scale';
import { Dictionary } from 'lodash';
import React, { useCallback } from 'react';
import { oldTheme } from '../../shared/old-styles';
import { getTextColor } from '../../shared/palettes';
import { noTextSelection, theme } from '../../shared/theme';
import type { KeysPressOptions } from '../legends/legend';
import { GenesBarStack } from './genes-bar-stack';
import { getSampleSubtypeOrTypeLabel } from './tooltip-sampling-point-utils';
import { ScaleBand, ScaleTime } from './multi-plot';
import { BoxTooltipContent } from './tooltips';
import { getSampleSelector, getSemiTransparentColor, VisualSelectionStatus } from './utils';

export interface GroupsProps extends GroupProps {
  data: Array<BoxDatum[] | BarDatum[]>;
  colorScale: ScaleOrdinal<string, string> | undefined;
  barPlotGeneGroups: string[] | undefined;
  sortIdxByGene: Dictionary<number>;
  type: 'bar' | 'box' | 'heat';
  project: FullProject;
}

export function DataGroups(props: GroupsProps) {
  const { barPlotGeneGroups, colorScale } = props;
  if (!props.data.length) {
    return null;
  }
  const groupLengths = props.data.map(datums => datums.length);
  const groupLength = groupLengths.length ? Math.max(...groupLengths) : 0;

  if (barPlotGeneGroups && colorScale) {
    // Detected genes bar groups
    // OR heatmap bar groups (if no color detectedGenesColorScale provided)
    const data = props.data as BarDatum[][];

    return data.map((datums, i) => (
      <BarGroup
        i={i}
        key={`bargroup${i}`}
        datums={datums}
        {...props}
        isHeatmap={props.type === 'heat'}
        colorScale={colorScale}
        groupLength={groupLength}
        geneGroups={barPlotGeneGroups}
        totalGroupSize={data.length}
      />
    ));
  } else {
    // Box groups
    const data = props.data as BoxDatum[][];
    return data.map((datums, i) =>
      datums.length ? (
        <BoxGroup i={i} key={`boxgroup${i}`} groupLength={groupLength} datums={datums} {...props} />
      ) : null,
    );
  }
}

interface GroupProps {
  xScale: AxisScale<number>;
  yScale: ScaleLinear<number, number>;
  againstTime: boolean;
  groupWidth: number;
  ordinalAligmentFixup: number;
  tooltipStuff: UseTooltipInPortal;
  grouping: GeneGrouping;
  sortIdxByGene: Dictionary<number>;
  singleGeneHeatBoxHeight: number;
  showSampleNumbers: boolean;
  project: FullProject;
}

interface BarGroupPropsWoData extends GroupProps {
  i: number;
  geneGroups: string[];
  colorScale: ScaleOrdinal<string, string>;
  isHeatmap: boolean;
  groupLength: number;
  project: FullProject;
}

export interface GeneBarStackProps extends BarGroupPropsWoData {
  datum: BarDatum;
  currentGroupLength: number;
  j: number;
  totalGroupSize: number;
  bioRepCount: number;
}

interface BarGroupProps extends BarGroupPropsWoData {
  datums: BarDatum[];
  totalGroupSize: number;
}

function BarGroup(props: BarGroupProps) {
  // Number of detected barstack group
  // OR heatmap barstack group (if detectedGenesColorScale not provided)
  const { i, datums } = props;
  const stackProps = { ...props };
  delete (stackProps as Partial<BarGroupProps>).datums;

  return (
    <Group>
      {datums.map((barDatum, j) => {
        return (
          <GenesBarStack
            key={`stackedbargroup-${i}-${j}`}
            {...stackProps}
            j={j}
            datum={barDatum}
            currentGroupLength={datums.length}
            totalGroupSize={props.totalGroupSize}
            bioRepCount={datums.length}
          />
        );
      })}
    </Group>
  );
}

interface BoxGroupProps extends GroupProps {
  i: number;
  datums: BoxDatum[];
  groupLength: number;
}

function BoxGroup(props: BoxGroupProps) {
  const { i, ordinalAligmentFixup, againstTime, datums, xScale, groupWidth: width, groupLength } = props;

  const scaledX = againstTime ? (xScale as ScaleTime)(new Date(datums[0].date)) : (xScale as ScaleBand)(`${i}`);
  const left = scaledX - 0.5 * width + ordinalAligmentFixup;
  const { groupLeftMargin, itemWidth } = getGroupItemWidthAndMargin(width, groupLength, datums.length);

  return (
    <Group left={left}>
      {datums.map((datum, j) => (
        <Box key={`box${j}`} j={j} {...props} groupLeftMargin={groupLeftMargin} itemWidth={itemWidth} datum={datum} />
      ))}
    </Group>
  );
}

interface BoxProps extends BoxGroupProps {
  datum: BoxDatum;
  itemWidth: number;
  groupLeftMargin: number;
  j: number;
}

function Box(props: BoxProps) {
  const { datum, yScale, j, groupLeftMargin, itemWidth, tooltipStuff } = props;
  const { queryFilters } = useSampleDataContext();
  const {
    samplesBeingSelected,
    setSamplesBeingSelected,
    samplingPointIdsForSelectedGroup,
    sampleById,
    getVisualSelectionStatus,
  } = useResearchContext();
  const { tooltipData, tooltipProps, handleMouseMove } = usePlotTooltip<BoxDatum>(tooltipStuff);
  const experimentalMetric = useExperimentalMetric();
  const normalisationMode = useNormalisationMode();
  const exprimentalMetricValue = experimentalMetric
    ? datum.experimentalMetrics?.[experimentalMetric]?.toFixed(experimentalMetric.startsWith('A') ? 1 : 0)
    : null;
  const xAxisMode = useXAxisMode();
  const nonQuantifiedMessage = isAyNormalisationMode(normalisationMode)
    ? 'Unclear amplification (no quantifiable value).'
    : 'Traces detected (no quantifiable value).';

  const visualStatus = getVisualSelectionStatus(datum.samplingPointId);

  const onClick = useCallback(
    (label: number, keyPresses: KeysPressOptions) =>
      getSampleSelector(
        queryFilters.filters.selectedSamplingPointIdsOrdered,
        samplingPointIdsForSelectedGroup,
        samplesBeingSelected,
        setSamplesBeingSelected,
        queryFilters.toggleSamplingPointStable,
        true,
        false,
      )(label, keyPresses),
    [
      queryFilters.filters.selectedSamplingPointIdsOrdered,
      samplingPointIdsForSelectedGroup,
      samplesBeingSelected,
      setSamplesBeingSelected,
      queryFilters.toggleSamplingPointStable,
    ],
  );

  const triggerProps = {
    onMouseOver: (event: React.MouseEvent<any>) => {
      handleMouseMove(event, datum, true);
    },
    onMouseLeave: (event: React.MouseEvent<any>) => {
      handleMouseMove(event, undefined);
    },
    onMouseMove: (event: React.MouseEvent<any>) => {
      handleMouseMove(event, datum);
    },
  };

  const isTraceOnly = datum.traceOnly === true;
  // If there is no proper data for a box, we still return an empty box with a tooltip mentioning there are no genes
  if (
    datum.min === undefined ||
    datum.max === undefined ||
    datum.firstQuartile === undefined ||
    datum.thirdQuartile === undefined ||
    datum.mean === undefined ||
    datum.outliers === undefined
  ) {
    const sampleLabel = xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL ? datum.bioNumber : datum.sampleNumber;
    const sampleSubtypeOrTypeLabel = getSampleSubtypeOrTypeLabel(sampleById.get(Number(datum.sampleId)));
    const [rangeStart, rangeEnd] = yScale.range();
    const rangeTop = Math.min(rangeStart, rangeEnd);
    const rangeBottom = Math.max(rangeStart, rangeEnd);
    const rangeSize = Math.max(rangeBottom - rangeTop, 1);
    const placeholderHeight = Math.min(rangeSize * 0.35, 32);
    const placeholderY = rangeTop + (rangeSize - placeholderHeight) / 2;
    const placeholderWidth = itemWidth * 0.6;
    const placeholderX = groupLeftMargin + j * itemWidth + (itemWidth - placeholderWidth) / 2;
    const traceColor = getSemiTransparentColor(datum.color);
    const placeholderFill =
      isTraceOnly && visualStatus !== VisualSelectionStatus.DESELECTED
        ? traceColor
        : isTraceOnly
          ? traceColor
          : 'transparent';
    const placeholderOpacity = isTraceOnly ? 1 : 0;
    const placeholderStroke = isTraceOnly ? traceColor : 'transparent';
    return (
      <Group
        onClick={(e: React.MouseEvent) => {
          onClick(datum.samplingPointId, getKeyPressesFromMouseClick(e));
        }}
        style={{ cursor: 'pointer' }}
      >
        {isTraceOnly ? (
          <rect
            x={placeholderX}
            y={placeholderY}
            width={placeholderWidth}
            height={placeholderHeight}
            fill={placeholderFill}
            fillOpacity={placeholderOpacity}
            stroke={placeholderStroke}
            strokeWidth={1}
            rx={4}
            ry={4}
            {...triggerProps}
          />
        ) : null}
        <BoxPlot
          left={groupLeftMargin + j * itemWidth}
          boxWidth={itemWidth}
          fillOpacity={1}
          fill="transparent"
          stroke="transparent"
          strokeWidth={1}
          valueScale={yScale}
          container={true}
          containerProps={triggerProps}
          boxProps={triggerProps}
          minProps={triggerProps}
          maxProps={triggerProps}
          outlierProps={{ ...triggerProps, style: { opacity: 0.2 } }}
        />
        {tooltipData && (
          <PlotTooltip {...tooltipProps}>
            <div>
              <div style={{ color: oldTheme.darkGray, padding: 8 }}>
                <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
                  <strong>Sample:</strong> {sampleLabel} ({tooltipData.sampleId})
                </div>
                <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
                  <strong>Name: </strong> {tooltipData.label}
                </div>
                {tooltipData.date ? (
                  <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
                    <strong>Date:</strong> {friendlyDate(tooltipData.date as StandardDateFormat, 'prefixMon', true)}
                  </div>
                ) : null}
                {sampleSubtypeOrTypeLabel ? (
                  <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 2 }}>
                    <strong>Type:</strong> {sampleSubtypeOrTypeLabel}
                  </div>
                ) : null}
              </div>
              <div style={{ fontSize: oldTheme.fontSize.s, marginTop: 8, marginLeft: 8 }}>
                <i>{isTraceOnly ? nonQuantifiedMessage : 'No genes detected!'}</i>
              </div>
            </div>
          </PlotTooltip>
        )}
      </Group>
    );
  }
  const medianColor =
    datum.firstQuartile === datum.thirdQuartile // No box
      ? theme.colors.neutral900
      : getTextColor(datum.color, theme.colors.neutral300, theme.colors.neutral900);
  const baseFillColor =
    visualStatus === VisualSelectionStatus.DESELECTED ? getSemiTransparentColor(datum.color) : datum.color;
  const baseFillOpacity = visualStatus !== VisualSelectionStatus.SELECTED ? 0.5 : 1;
  const fillColor = baseFillColor;
  const strokeColor = isTraceOnly ? getSemiTransparentColor(baseFillColor) : baseFillColor;
  const fillOpacity = isTraceOnly ? 0.5 : baseFillOpacity;

  return (
    <Group
      onClick={(e: React.MouseEvent) => {
        onClick(datum.samplingPointId, getKeyPressesFromMouseClick(e));
      }}
    >
      <BoxPlot
        left={groupLeftMargin + j * itemWidth}
        min={datum.min}
        max={datum.max}
        firstQuartile={datum.firstQuartile}
        thirdQuartile={datum.thirdQuartile}
        outliers={datum.outliers}
        median={datum.median}
        boxWidth={itemWidth}
        fillOpacity={fillOpacity}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={1}
        medianProps={{ stroke: medianColor }}
        valueScale={yScale}
        container={true}
        containerProps={{ ...triggerProps, style: { cursor: 'pointer' } }}
        boxProps={triggerProps}
        minProps={triggerProps}
        maxProps={triggerProps}
        outlierProps={{ ...triggerProps, style: { opacity: 0.2 } }}
      />
      <Croshair
        {...triggerProps}
        color={medianColor}
        left={groupLeftMargin + j * itemWidth + itemWidth / 2}
        top={props.yScale(datum.mean)}
      />
      {tooltipData && (
        <PlotTooltip {...tooltipProps}>
          <BoxTooltipContent tooltipData={tooltipData} />
        </PlotTooltip>
      )}
      {exprimentalMetricValue ? (
        <ExperimentalMetric
          top={265}
          left={groupLeftMargin + j * itemWidth + itemWidth / 2}
          value={exprimentalMetricValue}
          groupWidth={props.groupWidth}
        />
      ) : null}
    </Group>
  );
}

interface CroshairProps {
  left: number;
  top: number;
  color: string;
  onMouseOver: (event: React.MouseEvent<any>) => void;
  onMouseLeave: (event: React.MouseEvent<any>) => void;
  onMouseMove: (event: React.MouseEvent<any>) => void;
}

function Croshair({ onMouseOver, onMouseLeave, onMouseMove, left, top, color }: CroshairProps) {
  return (
    <X left={left} top={top}>
      <text
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
        fontSize={8}
        fill={color}
        textAnchor="middle"
        dy="0.3em"
      >
        {'✕'}
      </text>
    </X>
  );
}

export function ExperimentalMetric({
  value,
  left,
  top,
  groupWidth,
}: {
  value: string;
  left: number;
  top: number;
  groupWidth: number;
}) {
  return (
    <X left={left} top={top}>
      <text
        fontSize={groupWidth < 30 ? 10 : 14}
        fill={'black'}
        textAnchor="top"
        dy={groupWidth < 30 ? '12.3em' : '10.3em'}
      >
        {value}
      </text>
    </X>
  );
}

const X = styled(Glyph)`
  ${noTextSelection}
  cursor: pointer;
`;

export function getGroupItemWidthAndMargin(groupWidth: number, maxGroupLength: number, currentGroupLength: number) {
  // Group width has 0.1 margin on both sides, leaving 0.8 for bars/boxes
  const itemWidth = (0.8 * groupWidth) / maxGroupLength;
  const slack = ((maxGroupLength - currentGroupLength) * itemWidth) / 2; // align shorter groups in the middle of their group slot
  const groupLeftMargin = 0.1 * groupWidth + slack;
  return {
    itemWidth,
    groupLeftMargin,
  };
}
